import { DateAdapter } from "@angular/material/core";
import { STATISTICS_FILTER_SELECT_CONFIGS } from "app/shared/ss-multiselect-dropdown-settings.shared";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ApiService } from "app/services/api.service";
import { HelperService, ReportFilter, UiLang } from "app/services/helper.service";
import moment from "moment";
import { StatisticsService } from "app/services/statistics.service";
import { StatisticsDateOption } from "app/models/statistics.model";
import { NgClass, NgIf } from "@angular/common";
import { NgxBootstrapMultiselectModule } from "ngx-bootstrap-multiselect";
import { FormsModule, NgModel } from "@angular/forms";
import { AlertsTabComponent } from "./tabs/alerts-tab/alerts-tab.component";
import { AlarmCenterTabComponent } from "./tabs/alarm-center-tab/alarm-center-tab.component";
import { CareRecipientsTabComponent } from "./tabs/care-recipients-tab/care-recipients-tab.component";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatInputModule } from "@angular/material/input";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatExpansionModule } from "@angular/material/expansion";
import {
  Component,
  OnInit,
  DoCheck,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
  DestroyRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActiveUsersTabComponent } from "./tabs/active-users-tab/active-users-tab.component";
import { StatisticsPdfExportService } from "app/services/statistics-pdf-export.service";
import { StatisticsPngExportService } from "app/services/statistics-png-export.service";
import { buildStatisticsFilterSummary } from "app/services/statistics-filter-summary.util";
import {
  StatisticsTabExportSource,
  StatisticsPdfExportContext,
  StatisticsExportProgress,
  StatisticsExportSnapshot,
} from "app/services/statistics-export.models";
@Component({
    selector: "statistics",
    templateUrl: "./statistics.component.html",
    styleUrls: ["./statistics.component.css"],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
    NgIf,
    NgClass,
    NgxBootstrapMultiselectModule,
    TranslateModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatProgressBarModule,
    MatExpansionModule,
    AlertsTabComponent,
    AlarmCenterTabComponent,
    CareRecipientsTabComponent,
    ActiveUsersTabComponent
]
})
export class StatisticsComponent implements OnInit, DoCheck {
  constructor(
    private dateAdapter: DateAdapter<any>,
    private translate: TranslateService,
    private api: ApiService,
    private helper: HelperService,
    public statistics: StatisticsService,
    private statisticsPdfExport: StatisticsPdfExportService,
    public pngExport: StatisticsPngExportService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) { }

  @ViewChild(AlertsTabComponent) alertsTab: AlertsTabComponent;
  @ViewChild(AlarmCenterTabComponent) alarmCenterTab: AlarmCenterTabComponent;
  @ViewChild(CareRecipientsTabComponent) careRecipientsTab: CareRecipientsTabComponent;
  @ViewChild(ActiveUsersTabComponent) activeUsersTab: ActiveUsersTabComponent;

  @ViewChild("dropdownBtn") dropdownBtn: ElementRef;
  @ViewChild("startDateInput") startDateInput: NgModel;
  @ViewChild("endDateInput") endDateInput: NgModel;

  private _selectedDateOption: StatisticsDateOption;
  selectedDateOptionText: string;

  startTime: string = "00:00";
  endTime: string = "23:59";

  selectedTab: "alerts" | "alarmCenter" | "careRecipients" | "activeUsers" = "alerts";
  filterSelects: ReportFilter = STATISTICS_FILTER_SELECT_CONFIGS;
  currentDate: Date = new Date();
  dateLimit: Date = moment().subtract(4, "months").toDate();
  showTimeOptions: boolean = false;
  EMAACEnabled: boolean = false;
  loadingPdf: boolean = false;
  /** True from the moment Search is clicked until the tab data finishes loading. */
  loadingData: boolean = false;
  /**
   * True when any filter/time setting has changed since the last explicit search
   * (or tab switch). Drives the step-box CTA highlight: when true → Box 2 (Search)
   * is highlighted; when false and export is ready → Box 3 (Export) is highlighted.
   */
  /**
   * Serialized filter state at the time of the last committed search (or tab switch).
   * null = no search committed yet; treated as "changed" so the Search button starts primary.
   */
  private committedFilters: string | null = null;

  private serializeFilters(): string {
    const f = this.statistics.filters;
    return JSON.stringify({
      dateOption: this._selectedDateOption,
      startDate: f.startDate?.getTime() ?? null,
      endDate: f.endDate?.getTime() ?? null,
      locations: [...(f.locations ?? [])].sort(),
      devices: [...(f.devices ?? [])].sort(),
      deviceTypes: [...(f.deviceTypes ?? [])].sort(),
      alertTypes: [...(f.alertTypes ?? [])].sort(),
      alertRoutes: [...(f.alertRoutes ?? [])].sort(),
      personnels: [...(f.personnels ?? [])].sort(),
    });
  }

  private commitFilters(): void {
    this.committedFilters = this.serializeFilters();
  }

  get filtersChangedSinceSearch(): boolean {
    if (this.committedFilters === null) return true;
    return this.committedFilters !== this.serializeFilters();
  }

  pdfExportProgress = 0;
  pdfExportStatusKey = 'LOADING_PDF';
  pdfError: boolean = false;
  pdfSuccess: boolean = false;

  /** Populated after translations load; avoid translate.instant() at class init. */
  dateOptions: IDateOptions | null = null;

  get selectedDateOption(): StatisticsDateOption {
    return this._selectedDateOption;
  }

  set selectedDateOption(option: IDateOption) {
    if (option.value !== StatisticsDateOption.Custom) {
      this.startTime = "00:00";
      this.endTime = "23:59";
    }
    this.statistics.filters.setDates(option.value);
    this.statistics.filters.applyTimes(this.startTime, this.endTime);
    this._selectedDateOption = option.value;
    this.selectedDateOptionText = option.text;
  }

  /**
   * Card dropdown hide handler
   * @param event Click event
   */
  @HostListener("document:click", ["$event"])
  handleHideDropdown(event: Event): void {
    if (!this.dropdownBtn?.nativeElement) {
      return;
    }

    // Check if clicked element is dropdown button or element inside the button
    const isDropdownBtn = this.dropdownBtn.nativeElement === event.target;
    const isBtnInnerEl = this.dropdownBtn.nativeElement.contains(event.target);

    // Hide the dropdown if the clicked element isn't any of the checked elements
    if (!isDropdownBtn && !isBtnInnerEl) {
      this.showTimeOptions = false;
    }
  }

  onDateSelect(selectedOption: IDateOption): void {
    if (selectedOption.value === this._selectedDateOption) return;
    this.selectedDateOption = selectedOption;
  }

  /** True when any filter or time setting differs from its default — controls the "Clear filters" button */
  get hasActiveFilters(): boolean {
    const f = this.statistics.filters;
    return (
      [f.locations, f.devices, f.deviceTypes, f.alertTypes, f.alertRoutes, f.personnels].some(
        (list) => list?.length > 0
      ) ||
      this.startTime !== "00:00" ||
      this.endTime !== "23:59" ||
      (this._selectedDateOption != null && this._selectedDateOption !== StatisticsDateOption.Today)
    );
  }

  resetFilters(): void {
    this.statistics.filters.locations = [];
    this.statistics.filters.alertRoutes = [];
    this.statistics.filters.devices = [];
    this.statistics.filters.deviceTypes = [];
    this.statistics.filters.alertTypes = [];
    this.statistics.filters.personnels = [];
    this.startTime = "00:00";
    this.endTime = "23:59";
    if (this.dateOptions) {
      this.selectedDateOption = this.dateOptions.today;
    }
  }

  onCustomStartDateSelect(event: any): void {
    this.statistics.filters.startDate = moment(event.value).startOf("d").toDate();
    this.statistics.filters.applyTimes(this.startTime, this.endTime);
  }

  onCustomEndDateSelect(event: any): void {
    this.statistics.filters.endDate = moment(event.value).endOf("d").toDate();
    this.statistics.filters.applyTimes(this.startTime, this.endTime);
  }

  onTimeChange(): void {
    this.statistics.filters.applyTimes(this.startTime, this.endTime);
  }

  get endTimeConflict(): boolean {
    if (this._selectedDateOption !== StatisticsDateOption.Custom) return false;
    const f = this.statistics.filters;
    // endDate equals startDate only when the hard clamp in applyTimes was triggered
    return f.endDate.getTime() === f.startDate.getTime();
  }

  compareNames(a: any, b: any): number {
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
  }

  getDeviceTypeNames(deviceTypes: string[]): { id: string; name: string }[] {
    const array = [];
    for (const deviceType of deviceTypes) {
      array.push({
        id: deviceType,
        name: this.api.getDeviceType(deviceType, true),
      });
    }
    return array;
  }

  onDeviceTypeFilterChange(deviceTypes: string[]): void {
    const selectedTypes = deviceTypes ?? [];
    const filterList: any[] = [];

    for (const deviceType of selectedTypes) {
      const activations: any[] = this.helper.getActivationsForDeviceType(deviceType);
      if (activations.length === 0) continue;

      // With several device types selected, group the alert types under a
      // device type heading so identically named activations can be told apart
      if (selectedTypes.length > 1) {
        filterList.push({
          id: deviceType,
          name: this.api.getDeviceType(deviceType, true),
          isLabel: true,
        });
      }

      for (const e of activations) {
        filterList.push({
          id: `${e.group};${e.node};${e.deviceType}`,
          name: e.name,
          parentId: selectedTypes.length > 1 ? deviceType : undefined,
        });
      }
    }

    this.filterSelects.activations.filterList = filterList;

    // Keep alert type selections that still belong to a selected device type
    const validIds = new Set(filterList.filter((o) => !o.isLabel).map((o) => o.id));
    this.statistics.filters.alertTypes = (this.statistics.filters.alertTypes ?? []).filter((id) =>
      validIds.has(id)
    );
  }

  initFilterSelects(): void {
    // Get select options for the filterSelects
    this.api
      .getReportFilteringOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      // Without filter options (none, or a failed request) the filters just stay empty
      .subscribe({ error: () => {}, next: (res) => {
      if (!res) return;
      this.filterSelects.device.filterList = res.devices.sort(
        this.compareNames
      );
      this.filterSelects.location.filterList = res.locations.sort(
        this.compareNames
      );
      this.filterSelects.personnel.filterList = res.personnel.sort(
        this.compareNames
      );
      this.filterSelects.alertRoute.filterList = res.alertRoutes.sort(
        this.compareNames
      );
      this.filterSelects.deviceTypes.filterList = this.getDeviceTypeNames(
        res.deviceTypes
      ).sort(this.compareNames);
    } });

    this.translate
      .get(this.getFilterTranslationKeys())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((t) => {
      const previousDateValue = this._selectedDateOption;
      this.dateOptions = this.buildDateOptions(t);
      this.applyFilterSelectTranslations(t);

      if (previousDateValue == null) {
        this.selectedDateOption = this.dateOptions.today;
      } else {
        this.syncSelectedDateOptionText();
      }

      this.cdr.detectChanges();
    });
  }

  private getFilterTranslationKeys(): string[] {
    return [
      "TODAY",
      "YESTERDAY",
      "LAST_WEEK",
      "LAST_MONTH",
      "LAST_90_DAYS",
      "CUSTOM_DATES",
      "DATE_FROM",
      "DATE_TO",
      "FILTER_CHECK_ALL",
      "FILTER_UNCHECK_ALL",
      "FILTER_CHECKED",
      "FILTER_ALL_SELECTED",
      "SEARCH",
      "DEVICE",
      "ALERT_ROUTE",
      "LOCATION",
      "BASESTATION",
      "STAFF",
      "CUSTOM_DEVICE_ID",
      "CUSTOM_BASESTATION_ID",
      "DEVICE_TYPE",
      "ACTIVATION",
      "EVENT_TYPE",
    ];
  }

  private buildDateOptions(t: Record<string, string>): IDateOptions {
    return {
      today: {
        value: StatisticsDateOption.Today,
        text: t.TODAY,
      },
      yesterday: {
        value: StatisticsDateOption.YesterDay,
        text: t.YESTERDAY,
      },
      lastWeek: {
        value: StatisticsDateOption.LastWeek,
        text: t.LAST_WEEK,
      },
      lastMonth: {
        value: StatisticsDateOption.LastMonth,
        text: t.LAST_MONTH,
      },
      last90Days: {
        value: StatisticsDateOption.Last90Days,
        text: t.LAST_90_DAYS,
      },
      custom: {
        value: StatisticsDateOption.Custom,
        text: t.CUSTOM_DATES,
      },
    };
  }

  private syncSelectedDateOptionText(): void {
    if (!this.dateOptions || this._selectedDateOption == null) {
      return;
    }

    const option = Object.values(this.dateOptions).find(
      (entry) => entry.value === this._selectedDateOption
    );
    if (option) {
      this.selectedDateOptionText = option.text;
    }
  }

  private applyFilterSelectTranslations(t: Record<string, string>): void {
    const shared = {
      checkAll: t.FILTER_CHECK_ALL,
      uncheckAll: t.FILTER_UNCHECK_ALL,
      checked: t.FILTER_CHECKED,
      checkedPlural: t.FILTER_CHECKED,
      searchPlaceholder: t.SEARCH,
      allSelected: t.FILTER_ALL_SELECTED,
    };

    this.filterSelects.device.texts = {
      ...shared,
      ...{ defaultTitle: t.DEVICE },
    };

    this.filterSelects.alertRoute.texts = {
      ...shared,
      ...{ defaultTitle: t.ALERT_ROUTE },
    };

    this.filterSelects.location.texts = {
      ...shared,
      ...{ defaultTitle: t.LOCATION },
    };

    this.filterSelects.basestation.texts = {
      ...shared,
      ...{ defaultTitle: t.BASESTATION },
    };

    this.filterSelects.personnel.texts = {
      ...shared,
      ...{ defaultTitle: t.STAFF },
    };

    this.filterSelects.deviceTypes.texts = {
      ...shared,
      ...{ defaultTitle: t.DEVICE_TYPE },
    };

    this.filterSelects.activations.texts = {
      ...shared,
      ...{ defaultTitle: t.ACTIVATION },
    };

    this.filterSelects.eventTypes.texts = {
      ...shared,
      ...{ defaultTitle: t.EVENT_TYPE },
    };
  }

  /**
   * Features are loaded asynchronously in global.component when localStorage is empty.
   * Reading once in ngOnInit races that fetch and can hide the Alarm Center tab until reload.
   */
  private resolveEmaacFeature(): void {
    if (localStorage.getItem("features")) {
      this.EMAACEnabled = this.helper.isFeatureEnabled("EMAAC");
      return;
    }

    this.api
      .getFeatures(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (response: string[]) => {
        localStorage.setItem("features", JSON.stringify(response));
        this.EMAACEnabled =
          Array.isArray(response) && response.includes("EMAAC");
        this.cdr.detectChanges();
      },
      error: () => {
        this.EMAACEnabled = false;
      },
    });
  }

  exportJSON(): void {
    switch (this.selectedTab) {
      case "alerts":
        this.statistics.exportAlertStatisticsJSON();
        break;
      case "alarmCenter":
        this.statistics.exportEmaacStatisticsJSON();
        break;
      case "careRecipients":
        this.statistics.exportCareRecipientStatisticsJSON();
        break;
      case "activeUsers":
        this.statistics.exportActiveUsersStatisticsJSON();
        break;
    }
  }

  selectTab(tab: "alerts" | "alarmCenter" | "careRecipients" | "activeUsers"): void {
    // Block tab changes while an export is running: the capture holds live ViewChild
    // references and must not have the active component replaced mid-flight.
    if (this.loadingPdf || this.pngExport.captureInProgress || this.selectedTab === tab) {
      return;
    }
    this.clearPdfExportStatus();
    this.selectedTab = tab;
    this.pngExport.currentTabKey = tab;

    // Cache hit: the tab component stays mounted via [hidden] with fresh data —
    // just commit the current filter state and skip the reload entirely.
    if (this.statistics.isTabCacheValid(tab, this.serializeFilters())) {
      this.commitFilters();
      return;
    }

    // Cache miss: reset the tab's ready state and trigger a fresh load.
    this.commitFilters();
    this.resetExportReadyForTab(tab);
    this.loadingData = true;
  }

  onSearchTabData(): void {
    // Explicit search always bypasses the cache for the active tab.
    this.statistics.invalidateTabCache(this.selectedTab);
    this.commitFilters();
    this.resetExportReadyForTab(this.selectedTab);
    this.clearPdfExportStatus();
    this.loadingData = true;
    this.statistics.getStatisticsTabData(this.selectedTab);
  }

  ngDoCheck(): void {
    const dataReady = this.isExportReady() || this.statistics.isTabExportReady(this.selectedTab);
    if (this.loadingData && dataReady) {
      this.loadingData = false;
      this.statistics.setTabCacheKey(this.selectedTab, this.serializeFilters());
    } else if (this.committedFilters === null && dataReady) {
      // Initial auto-load: no Search was clicked, so commit and prime the cache.
      this.commitFilters();
      this.statistics.setTabCacheKey(this.selectedTab, this.serializeFilters());
    }
  }

  private clearPdfExportStatus(): void {
    this.pdfSuccess = false;
    this.pdfError = false;
  }

  /** Reset export-ready state for a tab before it (re)loads data. */
  private resetExportReadyForTab(
    tab: "alerts" | "alarmCenter" | "careRecipients" | "activeUsers"
  ): void {
    switch (tab) {
      case "alerts":
        this.statistics.alertsTabDataLoaded = false;
        this.statistics.alertsTabPagingComplete = false;
        break;
      case "alarmCenter":
        this.statistics.alarmCenterTabDataLoaded = false;
        this.statistics.alarmCenterTabPagingComplete = false;
        break;
      case "careRecipients":
        this.statistics.careRecipientCardsLoaded = false;
        break;
      case "activeUsers":
        this.statistics.activeUsersTabDataLoaded = false;
        break;
    }
  }

  isExportReady(): boolean {
    if (!this.statistics.isTabExportReady(this.selectedTab)) {
      return false;
    }
    if (this.selectedTab === 'careRecipients') {
      // Only allow export when the user has added at least one care recipient card.
      return (this.statistics.careRecipientCards?.flat().length ?? 0) > 0;
    }
    return true;
  }

  async exportPdf(): Promise<void> {
    if (!this.isExportReady() || this.loadingPdf) {
      return;
    }

    const exportSource = this.getActiveTabExportSource();
    if (!exportSource) {
      return;
    }

    this.loadingPdf = true;
    this.pdfError = false;
    this.pdfSuccess = false;
    this.resetPdfExportProgress();
    this.cdr.detectChanges();
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    try {
      const exportContext: StatisticsPdfExportContext = {
        tab: this.selectedTab,
        dateOptionText: this.selectedDateOptionText,
        startDate: this.statistics.filters.startDate,
        endDate: this.statistics.filters.endDate,
        filterLines: buildStatisticsFilterSummary({
          tab: this.selectedTab,
          filters: this.statistics.filters,
          filterSelects: this.filterSelects,
          allLabel: this.translate.instant('FILTER_ALL_SELECTED'),
        }),
        snapshots: [],
        kpis: exportSource.getExportKpis(),
      };

      exportContext.snapshots = await exportSource.collectExportSnapshots((progress) =>
        this.updatePdfExportProgress(progress)
      );
      if (!exportContext.snapshots.length) {
        throw new Error('No export snapshots captured');
      }

      await this.statisticsPdfExport.export(exportContext, (progress) =>
        this.updatePdfExportProgress(progress)
      );
      // The PDF blob is built and saved — release the captured image data URLs
      // now (they can total hundreds of MB) instead of waiting for GC.
      this.releaseSnapshotImageData(exportContext.snapshots);
      this.updatePdfExportProgress({ phase: 'generate', current: 1, total: 1, messageKey: 'LOADING_PDF' });
      this.pdfExportProgress = 100;
      this.pdfSuccess = true;
    } catch (error) {
      console.error(error);
      this.pdfError = true;
    } finally {
      this.loadingPdf = false;
      this.resetPdfExportProgress();
      exportSource.restoreViewAfterExport?.();
      this.cdr.detectChanges();
    }
  }

  /**
   * Drop captured image data URLs once the PDF blob has been built so the (often
   * large) base64 strings are freed promptly rather than lingering until GC.
   */
  private releaseSnapshotImageData(snapshots: StatisticsExportSnapshot[] | undefined): void {
    if (!snapshots) {
      return;
    }
    for (const snapshot of snapshots) {
      snapshot.imageDataUrl = '';
      snapshot.squareChartImageDataUrl = undefined;
      snapshot.pdfSegments = undefined;
    }
    snapshots.length = 0;
  }

  private resetPdfExportProgress(): void {
    this.pdfExportProgress = 0;
    this.pdfExportStatusKey = 'LOADING_PDF';
  }

  private updatePdfExportProgress(progress: StatisticsExportProgress): void {
    const safeTotal = Math.max(1, progress.total);
    const ratio = Math.min(1, Math.max(0, progress.current / safeTotal));

    if (progress.phase === 'capture') {
      this.pdfExportProgress = Math.round(5 + ratio * 65);
    } else if (progress.phase === 'prepare') {
      this.pdfExportProgress = Math.round(70 + ratio * 20);
    } else {
      this.pdfExportProgress = Math.round(90 + ratio * 10);
    }

    this.pdfExportStatusKey = progress.messageKey ?? 'LOADING_PDF';
    this.cdr.detectChanges();
  }

  private getActiveTabExportSource(): StatisticsTabExportSource | null {
    switch (this.selectedTab) {
      case 'alerts':
        return this.alertsTab ?? null;
      case 'alarmCenter':
        return this.alarmCenterTab ?? null;
      case 'careRecipients':
        return this.careRecipientsTab ?? null;
      case 'activeUsers':
        return this.activeUsersTab ?? null;
      default:
        return null;
    }
  }

  ngOnInit(): void {
    const user = JSON.parse(localStorage.getItem("user"));
    user.language = localStorage.getItem("language");

    this.helper.setDateAdapterLocale(user.language as UiLang, this.dateAdapter);

    this.resolveEmaacFeature();
    this.initFilterSelects();

    this.pngExport.currentTabKey = this.selectedTab;
    // The default tab auto-loads data when its component mounts. Mark loading so the
    // Search button is disabled during the initial fetch, just as it is for manual searches.
    this.loadingData = true;
  }
}

interface IDateOptions {
  today: IDateOption;
  yesterday: IDateOption;
  lastWeek: IDateOption;
  lastMonth: IDateOption;
  last90Days: IDateOption;
  custom: IDateOption;
}

interface IDateOption {
  value: StatisticsDateOption;
  text: string;
}
